import { lazy, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UnicornApi } from '@cld/unicorn-api';
import { modules } from '../../components/side-menu/side-menu.constants.js';
import { RemoteModule } from '../../components/remote-module/remote-module.js';
import { extractBasename } from './extractBasename.js';

const RemoteTransformationCenter = lazy(() =>
  import('@cld-remote/transformation_center/index').then(({ TransformationCenter }) => ({
    default: TransformationCenter,
  }))
);

export const TransformationCenterPage = ({ unicornApi }: { unicornApi: UnicornApi }) => {
  const { productEnvironment } = unicornApi.data;
  const basename = extractBasename('center');
  const navigate = useNavigate();

  const pageData = useMemo(
    () => ({
      sdkCredentials: {
        cloudName: productEnvironment.cloudName,
        apiKey: productEnvironment.apiKey ?? '<unknown>',
        apiSecret: '<your_api_secret>',
      },
    }),
    [productEnvironment.externalId]
  );

  const navigateToTransformationBuilder = useCallback(
    (params: { transformationString?: string; publicId?: string; mediaType?: string } = {}) => {
      const searchParams = new URLSearchParams(params).toString();

      if (searchParams) {
        navigate(`../../${modules.transformationBuilder}?${searchParams}`);
      } else {
        navigate(`../../${modules.transformationBuilder}`);
      }
    },
    [navigate]
  );

  const navigateToOptimizeAndDeliverPage = useCallback(() => {
    navigate(`/${modules.optimizationCenter}`);
  }, [navigate]);

  const navigateToApiKeysSettings = useCallback(
    () => (window.location.href = `/settings/c-${productEnvironment.externalId}/api-keys`),
    [productEnvironment.externalId]
  );

  return (
    <RemoteModule>
      <RemoteTransformationCenter
        basename={basename}
        unicornApi={unicornApi}
        sdkCredentials={pageData.sdkCredentials}
        navigateToTransformationBuilder={navigateToTransformationBuilder}
        navigateToOptimizeAndDeliverPage={navigateToOptimizeAndDeliverPage}
        navigateToApiKeysSettings={navigateToApiKeysSettings}
      />
    </RemoteModule>
  );
};
